import { ArrowCcwIcon, TrashIcon } from "@xola/icons";
import { Modal, SubmitButton, Table, Tooltip } from "@xola/ui-kit";
import clsx from "clsx";
import { getExperiencePurchaseItems, handleSuccessfulSubmission, PurchasePriceStrategy } from "common";
import { pick } from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import { useMutation } from "react-query";
import { LineItem, LineItemType, Purchase, PurchaseItem, PurchaseStatus } from "types";
import { api } from "../../api/api";
import { useInvalidate } from "../../hooks/useInvalidation";
import { isPriceConfigurationUpdated } from "../../models/PurchaseItem";
import {
    CreatePurchaseItemLineItem,
    CreatePurchaseLineItem,
    CreatePurchaseState,
    useCreatePurchaseStore,
} from "../../stores/createPurchaseStore";
import { Currency } from "../Common/Currency";
import { SecondaryButton } from "../Common/SecondaryButton";
import { allowedKeysToMapPurchaseToStore } from "../PurchaseCreate/helpers/PurchaseCreateHelpers";
import { usePurchaseResponseStore } from "../PurchaseCreate/store/usePurchaseResponseStore";
import { lineItemsToIgnoreWhenApplyingCode } from "../PurchasesMenu/ApplySubmitCodeHelper";
import { PaymentBreakdown, PriceStrategy } from "./PaymentBreakdown";
import { PurchaseItemSelect } from "./PurchaseItemSelect";
import { shouldRetainPriceByDefault } from "@/models/Seller";

interface EditPurchaseTaxesAndFeesModalProps {
    isOpen: boolean;
    purchase: Purchase | CreatePurchaseState;
    item: PurchaseItem;
    onClose: () => void;
}

export const EditPurchaseTaxesAndFeesModal = ({
    isOpen,
    purchase,
    item,
    onClose,
}: EditPurchaseTaxesAndFeesModalProps) => {
    return (
        <Modal isOpen={isOpen} size="huge" onClose={onClose}>
            <ModalContent purchase={purchase} item={item} onClose={onClose} />
        </Modal>
    );
};

interface ModalContentProps {
    purchase: Purchase | CreatePurchaseState;
    item: PurchaseItem;
    onClose: () => void;
}

interface EditPurchaseTaxesAndFeesPayload {
    items: EditPurchaseItemPayload[];
}

interface EditPurchaseItemPayload {
    id: ID;
    lineItems: PayloadLineItem[];
    _priceStrategy?: PriceStrategy;
}

interface PayloadLineItem {
    id: ID;
    includeInAmount: boolean;
}

const ModalContent = ({ purchase, item, onClose }: ModalContentProps) => {
    const [isFetching, setIsFetching] = useState(false);
    const [selectedItem, setSelectedItem] = useState<PurchaseItem>(item);
    const [lineItems, setLineItems] = useState(selectedItem.lineItems);
    const invalidate = useInvalidate();
    const [showRetainPriceToggle, setShowRetainPriceToggle] = useState(false);
    const [retainPrice, setRetainPrice] = useState(!shouldRetainPriceByDefault());
    const experiencePurchaseItems = getExperiencePurchaseItems(purchase as Purchase);

    const getTaxesAndFeesLineItems = () => {
        return lineItems.filter((lineItem) => lineItem.type === LineItemType.TAX || lineItem.type === LineItemType.FEE);
    };

    const onToggleDelete = (toggledItem: LineItem) => {
        setLineItems(
            getTaxesAndFeesLineItems().map((lineItem: LineItem) => {
                if (toggledItem.id === lineItem.id) {
                    return { ...toggledItem, includeInAmount: !toggledItem.includeInAmount };
                }

                return lineItem;
            }),
        );
    };

    const {
        mutateAsync: modifyTaxesFees,
        isLoading: isLoadingModifyTaxesFees,
        isSuccess: isSuccessModifyTaxesFees,
    } = useMutation(async (payload: EditPurchaseTaxesAndFeesPayload) => {
        const { data } = await api.patch<Purchase>(`/purchases/${purchase.id}`, payload);

        if (purchase.status === PurchaseStatus.DRAFT) {
            usePurchaseResponseStore.getState().actions.setPurchase(data);
            useCreatePurchaseStore.getState().actions.setPurchase((draft) => {
                for (const [index, item] of data.items.entries()) {
                    const lineItems = item.lineItems
                        .filter((lineItem) => !lineItemsToIgnoreWhenApplyingCode.includes(lineItem.type))
                        .map((lineItem) => pick(lineItem, allowedKeysToMapPurchaseToStore));

                    draft.items[index].lineItems = lineItems as CreatePurchaseItemLineItem[];
                }

                draft.lineItems = data.lineItems.map((lineItem: LineItem) => {
                    return pick(lineItem, allowedKeysToMapPurchaseToStore) as CreatePurchaseLineItem;
                });
            });
            useCreatePurchaseStore.setState({
                balance: data.balance,
                dueNow: data.dueNow,
                amount: data.amount,
            });
        }
    });

    useEffect(() => {
        if (isSuccessModifyTaxesFees || isLoadingModifyTaxesFees) {
            return;
        }

        setIsFetching(true);

        setSelectedItem(item);
        setLineItems(item.lineItems);

        const timer = setTimeout(() => setIsFetching(false), 500);
        return () => clearTimeout(timer);
    }, [purchase, item, isSuccessModifyTaxesFees, isLoadingModifyTaxesFees]);

    const updatedPurchase = useMemo(() => {
        if (lineItems) {
            const payloadLineItems: PayloadLineItem[] = lineItems
                .filter((lineItem) => lineItem.type === LineItemType.TAX || lineItem.type === LineItemType.FEE)
                .map((lineItem) => ({
                    id: lineItem.id,
                    includeInAmount: lineItem.includeInAmount,
                }));
            return {
                items: [
                    {
                        id: selectedItem.id,
                        lineItems: payloadLineItems,
                        _priceStrategy: showRetainPriceToggle && retainPrice ? PriceStrategy.RETAIN : undefined,
                    },
                ],
            };
        }
    }, [lineItems, selectedItem.id, retainPrice, showRetainPriceToggle]);

    const handleSubmit = async () => {
        const payloadLineItems: PayloadLineItem[] = getTaxesAndFeesLineItems().map((lineItem) => ({
            id: lineItem.id,
            includeInAmount: lineItem.includeInAmount,
        }));
        const payload: EditPurchaseTaxesAndFeesPayload = {
            items: [
                {
                    id: selectedItem.id,
                    lineItems: payloadLineItems,
                    _priceStrategy: showRetainPriceToggle && retainPrice ? PriceStrategy.RETAIN : undefined,
                },
            ],
        };
        await modifyTaxesFees(payload);
        handleSuccessfulSubmission(() => {
            invalidate("purchases", purchase.id);
            onClose();
        });
    };

    const isLoadingPurchase = isLoadingModifyTaxesFees || isSuccessModifyTaxesFees ? false : isFetching;

    return (
        <>
            <Modal.Header>Modify Taxes &amp; Fees</Modal.Header>

            <Modal.Body className="flex-col space-y-4">
                <PurchaseItemSelect
                    items={experiencePurchaseItems}
                    value={selectedItem}
                    className="flex-1"
                    onChange={(item: PurchaseItem) => {
                        setSelectedItem(item);
                        setLineItems(item.lineItems);
                    }}
                />
                <div className="flex space-x-4">
                    <div className="flex-1 space-y-4">
                        <PurchasePriceStrategy
                            isChecked={retainPrice}
                            isVisible={showRetainPriceToggle}
                            onChange={() => setRetainPrice(!retainPrice)}
                        />
                        <Table>
                            <TaxesAndFeesHeader />
                            <Table.Body>
                                {getTaxesAndFeesLineItems().map((lineItem: LineItem) => (
                                    <TaxesAndFeesItem
                                        key={lineItem.id}
                                        lineItem={lineItem}
                                        onToggleDelete={onToggleDelete}
                                    />
                                ))}
                            </Table.Body>
                        </Table>
                    </div>
                    <div className="flex-1">
                        <div className="bg-gray-lighter p-6">
                            <PaymentBreakdown
                                isEditable={false}
                                purchase={{ ...purchase } as Purchase}
                                item={selectedItem}
                                payload={updatedPurchase}
                                isParentLoading={isLoadingPurchase}
                                onUpdate={(purchase) => {
                                    if (isPriceConfigurationUpdated(purchase, item) && !showRetainPriceToggle) {
                                        setShowRetainPriceToggle(true);
                                        setRetainPrice(!retainPrice);
                                    }
                                }}
                            />
                        </div>
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <fieldset className="flex justify-end space-x-4">
                    <SecondaryButton onClick={onClose}>
                        <div data-testid="edit-purchase-taxes-and-fees-modal-cancel-button">Cancel</div>
                    </SecondaryButton>

                    <SubmitButton
                        type="button"
                        isLoading={isLoadingModifyTaxesFees}
                        isSuccess={isSuccessModifyTaxesFees}
                        data-testid="edit-purchase-taxes-and-fees-modal-confirm-button"
                        onClick={handleSubmit}
                    >
                        Confirm
                    </SubmitButton>
                </fieldset>
            </Modal.Footer>
        </>
    );
};

interface TaxesAndFeesItemProps {
    lineItem: LineItem;
    onToggleDelete: (lineItem: LineItem) => void;
}

const TaxesAndFeesItem = ({ lineItem, onToggleDelete }: TaxesAndFeesItemProps) => {
    const { name, includeInAmount, amount } = lineItem;

    return (
        <Table.Row className={clsx(!includeInAmount && "line-through")}>
            <Tooltip placement="top" content={name} disabled={name.length < 15}>
                <Table.Cell className="no-translate !max-w-30 overflow-hidden text-ellipsis !pt-3">{name}</Table.Cell>
            </Tooltip>
            <Table.Cell>
                <Currency shouldRemoveTrailingZeroes={false}>{amount}</Currency>
            </Table.Cell>
            <Table.Cell>
                <SecondaryButton
                    size="small"
                    onClick={() => {
                        onToggleDelete(lineItem);
                    }}
                >
                    {includeInAmount ? <TrashIcon /> : <ArrowCcwIcon />}
                </SecondaryButton>
            </Table.Cell>
        </Table.Row>
    );
};

const TaxesAndFeesHeader = () => {
    return (
        <Table.Head>
            <Table.Row>
                <Table.Header>Name</Table.Header>
                <Table.Header>Amount</Table.Header>
                <Table.Header />
            </Table.Row>
        </Table.Head>
    );
};
